/**
 * Company branding + invoice config.
 *
 * Edit this file to change the data printed on every receipt.
 * Kept as a plain ES module so it can be imported anywhere without
 * pulling React or any framework.
 *
 * If you need per-database / per-tenant config, swap these values
 * with a fetch from /dashboard/api/company-info later — the rest of
 * the code reads from this single source.
 */
import { cur } from '@/lib/currency';

export const COMPANY = {
  // ── Identity ───────────────────────────────────────────────────────────
  name: 'شركة الفتح للتجارة',
  nameEn: 'Al Fath Trading Co.',
  legalForm: 'شركة ذات مسؤولية محدودة',

  // ── Logo ────────────────────────────────────────────────────────────────
  // Path under public/. To swap the logo, drop a new file in public/logo.png
  // — no other changes needed.
  logoUrl: '/icons/icon-512.png',

  // ── Address ─────────────────────────────────────────────────────────────
  address: {
    street: 'شارع الملك فهد، حي الورود',
    city: 'الرياض',
    region: 'المنطقة الوسطى',
    country: 'المملكة العربية السعودية',
    postalCode: '12345',
  },

  // ── Contact ─────────────────────────────────────────────────────────────
  phone: '+966 11 234 5678',
  mobile: '+966 50 123 4567',
  email: 'info@al-fath.co',
  website: 'https://el-fath.co',

  // ── Tax / legal ─────────────────────────────────────────────────────────
  taxId: '300123456700003',         // الرقم الضريبي
  commercialReg: '1010234567',      // السجل التجاري
  vatRate: 0.14,                    // 14% ضريبة قيمة مضافة (مصر)
  currency: 'EGP',                  // ISO code
  currencySymbol: 'ج.م',            // الجنيه المصري

  // ── Bank (optional, shows on invoices if filled) ───────────────────────
  bank: {
    name: 'البنك الأهلي السعودي',
    accountName: 'شركة الفتح للتجارة',
    iban: 'SA00 0000 0000 0000 0000 0000',
    accountNumber: '00000000000000',
  },

  // ── Receipt footer ──────────────────────────────────────────────────────
  receipt: {
    thankYouMessage: 'شكراً لتعاملكم معنا',
    returnPolicy: 'يمكن استرجاع البضاعة خلال 7 أيام بشرط وجود الفاتورة',
    socialNote: 'تابعنا على وسائل التواصل',
  },
};

// ── Currency formatting helpers ──────────────────────────────────────────
export function formatMoney(value, opts = {}) {
  const n = Number(value) || 0;
  const fractionDigits = opts.fractionDigits ?? 2;
  const withSymbol = opts.withSymbol ?? true;
  // Use Arabic-Indic digits if requested (ar-EG style)
  const locale = opts.locale || 'ar-EG';
  const formatted = n.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return withSymbol ? `${formatted} ${cur(COMPANY.currencySymbol)}` : formatted;
}

// ── Dates ────────────────────────────────────────────────────────────────
export function formatDate(value, opts = {}) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const locale = opts.locale || 'ar-EG';
  return d.toLocaleDateString(locale, {
    calendar: 'gregory',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...opts,
  });
}

export function formatDateTime(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ar-EG', {
    calendar: 'gregory',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Full company address as a single string ──────────────────────────────
export function getFullAddress() {
  const { street, city, region, country, postalCode } = COMPANY.address;
  return [street, `${city} ${postalCode}`, region, country].filter(Boolean).join('، ');
}
